import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  Agent,
  AppSlice,
  Avatar,
  FeedbackEntity,
  ModuleName,
  PageMode,
  StripeProducts,
  UsageDetails,
  User,
  Video,
} from "../types"

const initialState: AppSlice = {
  openedModule: null,
  selectedVideo: null,
  selectedAgent: null,
  pageMode: PageMode?.CREATE,
  selectedAvatar: null,
  avatars: [],
  avatarsLastUpdated: null,
  avatarsLoadingState: {
    lastLoadedId: "",
    isComplete: false,
    isLoading: false,
  },
  showStripeModal: false,
  feedbackModalState: {
    visibility: "hidden",
    entityId: null,
    entityType: null,
    openedFrom: null,
  },
  usageDetails: null,
  usageDetailsLoading: true,
  isUserAdmin: false,
  user: null,
  entitiesWithFeedback: new Set(),
  feedbackEntitiesLoaded: false,
  stripeProducts: null,
  stripeProductsLoading: false,
  stripeProductsError: null,
  stripeProductsLastFetch: null,
  avatarImageCache: {},
}

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    updateOpenedModule: (state, action: PayloadAction<ModuleName>) => {
      state.openedModule = action.payload
    },
    resetOpenedModule: (state) => {
      state.openedModule = null
    },
    updateSelectedVideo: (state, action: PayloadAction<Video>) => {
      state.selectedVideo = action.payload
    },
    resetSelectedVideo: (state) => {
      state.selectedVideo = null
    },
    updateSelectedAgent: (state, action: PayloadAction<Agent>) => {
      state.selectedAgent = action.payload
    },
    resetSelectedAgent: (state) => {
      state.selectedAgent = null
    },
    updatePageMode: (state, action: PayloadAction<PageMode>) => {
      state.pageMode = action.payload
    },
    resetPageMode: (state) => {
      state.pageMode = PageMode.CREATE
    },
    updateSelectedAvatar: (state, action: PayloadAction<Avatar>) => {
      state.selectedAvatar = action.payload
    },
    resetSelectedAvatar: (state) => {
      state.selectedAvatar = null
    },
    updateAvatars: (state, action: PayloadAction<Avatar[]>) => {
      state.avatars = action.payload
      state.avatarsLastUpdated = new Date()
    },
    appendAvatars: (state, action: PayloadAction<Avatar[]>) => {
      const existingIds = new Set(state.avatars.map((avatar) => avatar.id))
      const newAvatars = action.payload.filter(
        (avatar) => !existingIds.has(avatar.id),
      )
      state.avatars = [...state.avatars, ...newAvatars]
      state.avatarsLastUpdated = new Date()
    },
    addAvatar: (state, action: PayloadAction<Avatar>) => {
      state.avatars.unshift(action.payload)
      state.avatarsLoadingState = {
        lastLoadedId: "",
        isComplete: false,
        isLoading: false,
      }
    },
    deleteAvatar: (state, action: PayloadAction<Avatar>) => {
      state.avatars = state.avatars.filter(
        (avatar) => avatar.id !== action.payload.id,
      )
    },
    updateAvatar: (state, action: PayloadAction<Avatar>) => {
      state.avatars = state.avatars.map((avatar) =>
        avatar.id === action.payload.id ? action.payload : avatar,
      )
    },
    updateAvatarsLastUpdated: (state, action: PayloadAction<Date>) => {
      state.avatarsLastUpdated = action.payload
    },
    updateAvatarsLoadingState: (
      state,
      action: PayloadAction<{
        lastLoadedId?: string
        isComplete?: boolean
        isLoading?: boolean
      }>,
    ) => {
      state.avatarsLoadingState = {
        ...state.avatarsLoadingState,
        ...action.payload,
      }
    },
    resetAvatarsLoadingState: (state) => {
      state.avatarsLoadingState = {
        lastLoadedId: "",
        isComplete: false,
        isLoading: false,
      }
    },
    updateShowStripeModal: (state, action: PayloadAction<boolean>) => {
      state.showStripeModal = action.payload
    },
    updateUsageDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.usageDetailsLoading = action.payload
    },
    updateUsageDetails: (state, action: PayloadAction<UsageDetails>) => {
      state.usageDetails = action.payload
    },
    resetUsageDetails: (state) => {
      state.usageDetails = null
    },
    updateIsUserAdmin: (state, action: PayloadAction<boolean>) => {
      state.isUserAdmin = action.payload
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
    },
    updateFeedbackModalState: (
      state,
      action: PayloadAction<AppSlice["feedbackModalState"]>,
    ) => {
      state.feedbackModalState = action.payload
    },
    updateEntitiesWithFeedback: (
      state,
      action: PayloadAction<Set<FeedbackEntity>>,
    ) => {
      state.entitiesWithFeedback = action.payload
      state.feedbackEntitiesLoaded = true
    },
    addEntityWithFeedback: (state, action: PayloadAction<FeedbackEntity>) => {
      state.entitiesWithFeedback = new Set([
        ...state.entitiesWithFeedback,
        action.payload,
      ])
    },
    clearFeedbackState: (state) => {
      state.entitiesWithFeedback = new Set()
      state.feedbackEntitiesLoaded = false
    },
    setStripeProducts: (
      state,
      action: PayloadAction<{ products: StripeProducts; lastFetch: number }>,
    ) => {
      state.stripeProducts = action.payload.products
      state.stripeProductsLastFetch = action.payload.lastFetch
      state.stripeProductsError = null
    },
    setStripeProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.stripeProductsLoading = action.payload
    },
    setStripeProductsError: (state, action: PayloadAction<string | null>) => {
      state.stripeProductsError = action.payload
    },
    clearStripeProducts: (state) => {
      state.stripeProducts = null
      state.stripeProductsLoading = false
      state.stripeProductsError = null
      state.stripeProductsLastFetch = null
    },
    invalidateStripeProductsCache: (state) => {
      state.stripeProducts = null
      state.stripeProductsError = null
    },
    cacheAvatarImage: (
      state,
      action: PayloadAction<{ avatarId: string; imageUrl: string }>,
    ) => {
      if (!state.avatarImageCache) {
        state.avatarImageCache = {}
      }
      state.avatarImageCache[action.payload.avatarId] = action.payload.imageUrl
    },
    clearAvatarImageCache: (state) => {
      state.avatarImageCache = {}
    },
    resetState: () => initialState,
  },
})

export const AppActions = appSlice.actions
export default appSlice.reducer
